import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { vibeAgentLogger as logger } from 'v2/utils/logger'
import { useFlowId } from '../../hooks/use-flow-id'
import { useNodeLayout } from '../../hooks/use-node-layout'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { useFlowStore } from '../../store'
import type { GenericEdge, GenericNode } from '../../types'
import { diffWorkflowSnapshot, parseWorkflowSnapshot } from './canvas-snapshot-reconcile'
import { useWorkflowUpdater } from './use-workflow-updater'
import {
  isTerminalUpdate,
  readDraftSaveStatus,
  readSuggestedFlowName,
  UPDATE_TYPES,
  type VibeEvent,
} from './wire'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'

/**
 * The ONE canvas writer for background runs. Chat state never touches the
 * canvas; this hook consumes the same event feed separately:
 *
 * - live canvas events (node_added / edge_added / node_updated / *_configured)
 *   apply the event's carried workflow via the shared updater (id-based
 *   add/update, layout, positions);
 * - draft_saved reconciles delete-safe against the BE snapshot (the BE applied
 *   node_map server-side, so the carried workflow is already final), strips the
 *   one-shot `?new` param on the first save, and debounce-invalidates flowData
 *   to trailing-edge so a multi-save run doesn't re-render the builder per turn;
 * - snapshot reconcile (rehydration) removes what the authoritative snapshot
 *   dropped while the browser was detached, then add/updates the rest.
 */

// Events whose carried workflow is APPLIED (add/update semantics). Other
// workflow-carrying events (user_message, started, gates, completed — the
// mixed-deploy guard attaches workflow to those too) must NOT touch the
// canvas: applying them re-centered the viewport on every chat event.
const CANVAS_APPLY_EVENTS = new Set<string>([
  UPDATE_TYPES.NODE_ADDED,
  UPDATE_TYPES.EDGE_ADDED,
  UPDATE_TYPES.MULTIPLE_EDGES_ADDED,
  UPDATE_TYPES.NODE_UPDATED,
  UPDATE_TYPES.BATCH_OPERATION_COMPLETED,
  UPDATE_TYPES.SYSTEM_PROMPT_CONFIGURED,
  UPDATE_TYPES.AGENT_IDENTITY_CONFIGURED,
  UPDATE_TYPES.STT_GUIDANCE_CONFIGURED,
  UPDATE_TYPES.STT_KEYTERMS_CONFIGURED,
])

// Deletions carry the post-delete workflow; the shared updater only
// adds/updates, so these route through the delete-safe snapshot reconcile.
const CANVAS_DELETE_EVENTS = new Set<string>([
  UPDATE_TYPES.NODE_DELETED,
  UPDATE_TYPES.MULTIPLE_NODES_DELETED,
  UPDATE_TYPES.EDGE_DELETED,
])

// Legacy arrange cadence after a workflow apply.
const LAYOUT_UPDATE_DELAY_MS = 600
const FIT_VIEW_DELAY_MS = 600

const INVALIDATE_DEBOUNCE_MS = 2500

export const useCanvasSync = () => {
  const { applyWorkflowUpdate } = useWorkflowUpdater()
  const { getNodes, getEdges, deleteElements, setNodes, setEdges, fitView } = useTypedReactFlow()
  const { layoutNodes } = useNodeLayout()
  const { id: phloId } = useFlowId()
  const queryClient = useQueryClient()
  const navigate = useAppNavigate()

  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const arrangeTimers = useRef<{
    layout: ReturnType<typeof setTimeout> | null
    fitView: ReturnType<typeof setTimeout> | null
  }>({ layout: null, fitView: null })

  // Latest-ref pattern: consumers get stable callbacks; deps stay empty.
  const ref = useRef({
    applyWorkflowUpdate,
    getNodes,
    getEdges,
    deleteElements,
    setNodes,
    setEdges,
    fitView,
    layoutNodes,
    phloId,
  })
  ref.current = {
    applyWorkflowUpdate,
    getNodes,
    getEdges,
    deleteElements,
    setNodes,
    setEdges,
    fitView,
    layoutNodes,
    phloId,
  }

  useEffect(
    () => () => {
      if (invalidateTimer.current) clearTimeout(invalidateTimer.current)
      if (arrangeTimers.current.layout) clearTimeout(arrangeTimers.current.layout)
      if (arrangeTimers.current.fitView) clearTimeout(arrangeTimers.current.fitView)
    },
    [],
  )

  const reconcileFromSnapshot = useCallback(async (workflowJson?: string | null) => {
    const snapshot = parseWorkflowSnapshot(workflowJson)
    if (!snapshot) return
    const { getNodes, getEdges, deleteElements, applyWorkflowUpdate } = ref.current
    const diff = diffWorkflowSnapshot(
      { nodes: getNodes() as GenericNode[], edges: getEdges() as GenericEdge[] },
      snapshot,
    )
    if (diff.nodeIdsToRemove.length || diff.edgeIdsToRemove.length) {
      // deleteElements is async — the apply below reads getNodes(), so an
      // un-awaited delete races it on stale canvas state.
      await deleteElements({
        nodes: diff.nodeIdsToRemove.map((id) => ({ id })),
        edges: diff.edgeIdsToRemove.map((id) => ({ id })),
      })
    }
    // Unconditional: applyWorkflowUpdate re-diffs adds/updates by id+data and
    // no-ops when the canvas already matches the snapshot.
    applyWorkflowUpdate(workflowJson as string)
  }, [])

  const scheduleFlowDataInvalidate = useCallback(() => {
    if (invalidateTimer.current) clearTimeout(invalidateTimer.current)
    invalidateTimer.current = setTimeout(() => {
      const id = ref.current.phloId
      if (!id) return
      queryClient
        .invalidateQueries({ queryKey: flowQueryKeys().flowData(id) })
        .catch((e) => logger.warn('[VIBE_CANVAS] flowData invalidate failed', e))
    }, INVALIDATE_DEBOUNCE_MS)
  }, [queryClient])

  // One-shot: the first successful save makes the flow real — drop the `?new`
  // marker so a reload rehydrates (nothing else strips it on the BE-save path).
  // Through the ROUTER, not history.replaceState: react-router does not observe
  // raw replaceState, so useSearchParams consumers (isNew) would stay stale
  // until a reload.
  const stripNewParam = useCallback(() => {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('new')) return
    url.searchParams.delete('new')
    url.searchParams.delete('template')
    navigate(`${url.pathname}${url.search}`, { replace: true })
  }, [navigate])

  // Arrange agent-added nodes: BE workflows carry no positions, so without a
  // layout pass new nodes stack at the fallback position (legacy ran dagre
  // after every apply — same cadence here). Timers are tracked (superseded by
  // the next schedule, cleared on unmount) and guarded on phloId so an orphaned
  // callback after an in-builder flow switch never lays out the NEXT flow's
  // canvas.
  const arrangeAfterApply = useCallback(() => {
    const scheduledFor = ref.current.phloId
    if (arrangeTimers.current.layout) clearTimeout(arrangeTimers.current.layout)
    if (arrangeTimers.current.fitView) clearTimeout(arrangeTimers.current.fitView)
    arrangeTimers.current.fitView = null
    arrangeTimers.current.layout = setTimeout(() => {
      arrangeTimers.current.layout = null
      if (ref.current.phloId !== scheduledFor) return
      const { layoutNodes, setNodes, setEdges, fitView } = ref.current
      const { nodes, edges } = layoutNodes()
      setNodes(nodes)
      setEdges(edges)
      arrangeTimers.current.fitView = setTimeout(() => {
        arrangeTimers.current.fitView = null
        if (ref.current.phloId !== scheduledFor) return
        fitView({ maxZoom: nodes.length > 7 ? 0.8 : 1 })
      }, FIT_VIEW_DELAY_MS)
    }, LAYOUT_UPDATE_DELAY_MS)
  }, [])

  const onCanvasEvent = useCallback(
    (event: VibeEvent) => {
      if (event.update_type === UPDATE_TYPES.DRAFT_SAVED) {
        if (readDraftSaveStatus(event) !== 'failed') {
          reconcileFromSnapshot(event.workflow)
          stripNewParam()
          // NOTE: do NOT invalidate flowData here. Mid-run, the canvas is driven
          // by reconcile/apply events; a flowData refetch resolves to the older
          // persisted flow and the shared reseed effect (use-flow-props) would
          // clobber the live canvas back to it. The cache is synced once at run
          // terminal instead (below).
        }
        return
      }
      if (event.update_type === UPDATE_TYPES.FLOW_NAME_SUGGESTED) {
        // canvas-sync owns ALL flow-store writes: the streaming effect animates
        // the new name into the title field, which is the notification.
        const suggestedName = readSuggestedFlowName(event)
        if (suggestedName) {
          useFlowStore.getState().setShouldStreamFlowNameEffect(true)
          useFlowStore.getState().setCurrentFlowName(suggestedName)
        }
        return
      }
      if (isTerminalUpdate(event.update_type)) {
        // Legacy cleared the add-highlight at run end; without this the last
        // added node glows forever.
        useFlowStore.getState().setGlowingNodeID(null)
        // Sync the flowData cache to the final saved state ONCE the run is over,
        // so the next mount seeds from fresh data (no mid-run reseed clobber).
        // NOT for interrupted: its draft (row.workflow) can be NEWER than the
        // saved phlo — a reseed would clobber the reconciled draft. The resync
        // scheduler owns the interrupted true-up (reconcile vs reseed).
        if (event.update_type !== UPDATE_TYPES.INTERRUPTED) {
          scheduleFlowDataInvalidate()
        }
        return
      }
      if (CANVAS_DELETE_EVENTS.has(event.update_type)) {
        reconcileFromSnapshot(event.workflow)
        // Removals leave holes in the layout — reflow like additions do. The
        // 600ms arrange delay comfortably outlasts the async reconcile.
        arrangeAfterApply()
        return
      }
      if (CANVAS_APPLY_EVENTS.has(event.update_type) && event.workflow) {
        ref.current.applyWorkflowUpdate(event.workflow)
        // Re-arrange on every TOPOLOGY change (nodes appearing, edges rewiring
        // the graph) — but never on config-only node_updated / *_configured,
        // where a reflow would yank the viewport for an invisible change.
        // Bursts coalesce: each call supersedes the pending timers.
        if (
          event.update_type === UPDATE_TYPES.NODE_ADDED ||
          event.update_type === UPDATE_TYPES.BATCH_OPERATION_COMPLETED ||
          event.update_type === UPDATE_TYPES.EDGE_ADDED ||
          event.update_type === UPDATE_TYPES.MULTIPLE_EDGES_ADDED
        ) {
          arrangeAfterApply()
        }
      }
    },
    [arrangeAfterApply, reconcileFromSnapshot, scheduleFlowDataInvalidate, stripNewParam],
  )

  return { onCanvasEvent, reconcileFromSnapshot, scheduleFlowDataInvalidate }
}
